import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, Facebook, Instagram, Linkedin, MessageCircle, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const GOOGLE_SCRIPT_URL = process.env.REACT_APP_GOOGLE_SCRIPT_URL || '';

const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { valid: false, message: "Please enter a valid email format" };
  }
  const disposableDomains = [
    'tempmail.com', 'throwaway.com', 'fakeinbox.com', 'guerrillamail.com',
    'mailinator.com', '10minutemail.com', 'trashmail.com', 'temp-mail.org',
    'disposablemail.com', 'yopmail.com', 'getnada.com', 'maildrop.cc'
  ];
  const domain = email.split('@')[1]?.toLowerCase();
  if (disposableDomains.includes(domain)) {
    return { valid: false, message: "Please use a real email address" };
  }
  return { valid: true, message: "" };
};

export const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [emailError, setEmailError] = useState("");

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setForm({...form, email});
    if (email.length > 5) {
      const validation = validateEmail(email);
      setEmailError(validation.valid ? "" : validation.message);
    } else {
      setEmailError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailValidation = validateEmail(form.email);
    if (!emailValidation.valid) {
      setEmailError(emailValidation.message);
      toast.error(emailValidation.message);
      return;
    }
    setSending(true);
    try {
      if (!GOOGLE_SCRIPT_URL) {
        toast.error("Contact form is not configured yet. Please email us directly.");
        setSending(false);
        return;
      }
      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("email", form.email);
      formData.append("phone", form.phone);
      formData.append("company", form.company);
      formData.append("message", form.message);
      formData.append("source", "contact-page");
      await fetch(GOOGLE_SCRIPT_URL, { method: "POST", body: formData, mode: "no-cors" });
      toast.success("Message received. A response will follow within 24 hours.");
      setSubmitted(true);
    } catch (error) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  if (submitted) {
    return (
      <div>
        <section className="py-20 bg-slate-50 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <h1 className="text-4xl sm:text-5xl text-[#2D4A6D] mb-6 font-serif">Thank You</h1>
            <div className="w-16 h-0.5 bg-[#2D4A6D] mx-auto" />
          </div>
        </section>
        <section className="py-20 bg-white">
          <div className="max-w-lg mx-auto px-4 text-center">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
            <h2 className="text-2xl text-[#2D4A6D] mb-4 font-serif">Message Received</h2>
            <p className="text-slate-600 mb-6">A response will follow within 24 hours.</p>
            <button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", company: "", message: "" }); }} className="border-2 border-[#2D4A6D] text-[#2D4A6D] hover:bg-[#2D4A6D] hover:text-white px-6 py-3 transition-colors">
              Send Another Message
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <section className="py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl text-[#2D4A6D] mb-6 font-serif">Let's Connect</h1>
          <div className="w-16 h-0.5 bg-[#2D4A6D] mx-auto mb-6" />
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Discuss an existing workflow, operational requirement, system challenge, or upcoming project.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="lg:col-span-3">
              <h2 className="text-2xl text-[#2D4A6D] mb-8 font-serif">Send a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                    <input type="text" placeholder="John Smith" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required className="w-full h-12 px-4 border border-slate-200 focus:border-[#2D4A6D] focus:outline-none" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                    <input type="email" placeholder="john@company.com" value={form.email} onChange={handleEmailChange} required className={`w-full h-12 px-4 border focus:outline-none ${emailError ? 'border-red-500 bg-red-50' : 'border-slate-200 focus:border-[#2D4A6D]'}`} data-testid="contact-email-input" />
                    {emailError && <div className="flex items-center gap-2 mt-2 text-red-600 text-sm"><AlertCircle className="w-4 h-4" />{emailError}</div>}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Company</label>
                  <input type="text" placeholder="Company Name" value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} className="w-full h-12 px-4 border border-slate-200 focus:border-[#2D4A6D] focus:outline-none" data-testid="contact-company-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">What are you looking to accomplish?</label>
                  <textarea placeholder="Describe the operational requirement, workflow challenge, or project." value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} rows={5} className="w-full px-4 py-3 border border-slate-200 focus:border-[#2D4A6D] focus:outline-none resize-none" data-testid="contact-message-input" />
                </div>
                <button type="submit" disabled={sending || emailError} data-testid="contact-submit-button" className="w-full bg-[#2D4A6D] hover:bg-[#1F3A5F] text-white h-14 text-base font-medium disabled:opacity-50">
                  {sending ? "Sending..." : "Send Message"}
                </button>
              </form>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="lg:col-span-2">
              <h2 className="text-2xl text-[#2D4A6D] mb-8 font-serif">Contact Information</h2>
              
              <div className="space-y-4 mb-10">
                <a href="mailto:hello@donebyemma.com" className="flex items-center gap-4 p-4 bg-slate-50 border border-slate-200 hover:border-[#2D4A6D]/30 transition-colors">
                  <div className="w-12 h-12 bg-[#2D4A6D] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="text-[#2D4A6D] font-medium">hello@donebyemma.com</p>
                  </div>
                </a>
                <a href="https://wa.me/639178118457" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 bg-slate-50 border border-slate-200 hover:border-[#2D4A6D]/30 transition-colors">
                  <div className="w-12 h-12 bg-green-600 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">WhatsApp</p>
                    <p className="text-[#2D4A6D] font-medium">+63 917 811 8457</p>
                  </div>
                </a>
              </div>

              <h3 className="text-lg text-[#2D4A6D] mb-4">Connect</h3>
              <div className="flex gap-3 mb-10">
                {[
                  { href: "https://www.facebook.com/donebyemma/", icon: Facebook },
                  { href: "https://www.instagram.com/donebyemma/", icon: Instagram },
                  { href: "https://www.linkedin.com/in/emmaponce/", icon: Linkedin },
                  { href: "http://m.me/donebyemma", icon: MessageCircle }
                ].map((social, i) => (
                  <a key={i} href={social.href} target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-slate-100 hover:bg-[#2D4A6D] hover:text-white text-slate-600 flex items-center justify-center transition-colors">
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>

              <div className="p-6 bg-[#2D4A6D] text-white">
                <h3 className="text-lg font-semibold mb-4">What Happens Next?</h3>
                <div className="space-y-3">
                  {["Response within 24 hours", "Brief discovery call scheduled", "No obligation or pressure"].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-white/80 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-200">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl text-[#2D4A6D] mb-4 font-serif">Frequently Asked Questions</h2>
            <div className="w-16 h-0.5 bg-[#2D4A6D] mx-auto" />
          </div>
          <div className="space-y-6">
            {[
              { q: "What makes Done By Emma different?", a: "Done By Emma is built around operational leverage rather than task volume. The focus is on systems, proactive execution, workflow clarity, and reducing friction across the operation." },
              { q: "What is the minimum commitment?", a: "Engagement structure depends on scope, operational requirements, and the nature of the work. Specific terms are established before engagement." },
              { q: "How quickly can an engagement begin?", a: "Following the initial strategy call, onboarding timelines are determined by scope, system access, documentation requirements, and operational complexity." },
              { q: "Does Done By Emma work with teams or individual leaders?", a: "Both. Operational support can integrate directly with leadership or coordinate alongside an existing team structure." }
            ].map((faq, i) => (
              <div key={i} className="p-6 bg-white border border-slate-200">
                <h4 className="text-base font-semibold text-[#2D4A6D] mb-3">{faq.q}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
